import { useState } from "react";
import { Power, Waves, X } from "lucide-react";
import { timbroamAudioEngine } from "../audio/AudioEngine";
import type { InstrumentPlayerProps } from "../instruments/playerTypes";
import { useTimbroamState } from "../lib/appState";
import { DaegeumPlayer } from "../instruments/daegeum/DaegeumPlayer";
import { DidgeridooPlayer } from "../instruments/didgeridoo/DidgeridooPlayer";
import { ErhuPlayer } from "../instruments/erhu/ErhuPlayer";
import { GayageumPlayer } from "../instruments/gayageum/GayageumPlayer";
import { JangguPlayer } from "../instruments/janggu/JangguPlayer";
import { KoraPlayer } from "../instruments/kora/KoraPlayer";
import { MbiraPlayer } from "../instruments/mbira/MbiraPlayer";
import { NeyPlayer } from "../instruments/ney/NeyPlayer";
import { OudPlayer } from "../instruments/oud/OudPlayer";
import { SaronPlayer } from "../instruments/saron/SaronPlayer";
import { SitarPlayer } from "../instruments/sitar/SitarPlayer";
import { TablaPlayer } from "../instruments/tabla/TablaPlayer";
import { useI18n } from "../i18n/LocaleContext";
import { ErrorBoundary } from "./ErrorBoundary";

function renderInstrumentPlayer(props: InstrumentPlayerProps) {
  switch (props.instrument.id) {
    case "gayageum":
      return <GayageumPlayer {...props} />;
    case "erhu":
      return <ErhuPlayer {...props} />;
    case "oud":
      return <OudPlayer {...props} />;
    case "sitar":
      return <SitarPlayer {...props} />;
    case "daegeum":
      return <DaegeumPlayer {...props} />;
    case "ney":
      return <NeyPlayer {...props} />;
    case "janggu":
      return <JangguPlayer {...props} />;
    case "tabla":
      return <TablaPlayer {...props} />;
    case "mbira":
      return <MbiraPlayer {...props} />;
    case "kora":
      return <KoraPlayer {...props} />;
    case "yidaki_didgeridoo":
      return <DidgeridooPlayer {...props} />;
    case "gamelan_saron":
      return <SaronPlayer {...props} />;
    default:
      return <GayageumPlayer {...props} />;
  }
}

export function InstrumentShell(props: InstrumentPlayerProps) {
  const { state, dispatch } = useTimbroamState();
  const { t, getInstrumentText } = useI18n();
  const [showOnboarding, setShowOnboarding] = useState(!state.audioReady);
  const instrumentText = getInstrumentText(props.instrument);
  const enableAudio = async () => {
    try {
      const ready = await timbroamAudioEngine.ensureReady();
      dispatch({ type: "setAudioReady", audioReady: ready });
    } catch {
      dispatch({ type: "setAudioReady", audioReady: false });
    }
  };

  return (
    <section className="performance-surface instrument-shell" aria-label={`${props.instrument.name} interactive performance area`}>
      <div className="surface-header">
        <div>
          <p className="eyebrow">{instrumentText.family}</p>
          <h1>{props.instrument.name}</h1>
          <p className="surface-variant">{props.variant.name}</p>
        </div>
        <div className="surface-header-right">
          {!state.audioReady ? (
            <button type="button" className="audio-nudge-inline" aria-label={t.play.activateAudioFromStage} onClick={() => void enableAudio()}>
              <Power size={16} aria-hidden="true" />
              <span>{t.audio.enable}</span>
            </button>
          ) : (
            <div className="surface-state" aria-live="polite">
              <Waves size={18} aria-hidden="true" />
              {props.activeNotes.length > 0 ? `${props.activeNotes.length} ${t.play.active}` : t.play.ready}
            </div>
          )}
        </div>
      </div>
      {showOnboarding && !state.audioReady ? (
        <section className="onboarding-card" aria-label={t.play.quickStart}>
          <ol>
            {t.play.steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
          <button type="button" className="dismiss-button compact-dismiss" aria-label={t.play.dismissQuickStart} onClick={() => setShowOnboarding(false)}>
            <X size={16} aria-hidden="true" />
          </button>
        </section>
      ) : null}
      <div className="instrument-stage" data-testid="performance-area">
        <ErrorBoundary>{renderInstrumentPlayer(props)}</ErrorBoundary>
      </div>
    </section>
  );
}
